// src/diagrams/utils/NodeMeasurement.js - Accurate node dimension measurement for layout

/**
 * Node Measurement Utility for Contact Flow Diagrams
 * 
 * This utility measures the actual rendered dimensions of nodes before layout
 * to prevent overlapping when dynamic content makes nodes larger than estimated.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { categoryColors } from '../ContactFlowNodes';

// Flip to true to re-enable verbose per-node measurement diagnostics.
const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log(...args); };

// Base node styles - matching ContactFlowNodes.js
const baseNodeStyle = {
  border: '2px solid',
  borderRadius: '8px',
  background: 'white',
  padding: '12px',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
  minWidth: '160px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  cursor: 'pointer',
  boxSizing: 'border-box',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  // Measurement-specific positioning (doesn't affect size)
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none'
};

// Cache for measured dimensions
const measurementCache = new Map();

/**
 * Generates a cache key for node measurements
 */
const getCacheKey = (nodeType, data) => {
  if (nodeType === 'entryNode') {
    return `entry-${nodeType}`;
  }
  
  if (nodeType === 'startNode' || nodeType === 'endNode') {
    return `${nodeType}-${data.actionType || 'default'}`;
  }
  
  if (nodeType === 'decisionNode') {
    const display = data.displayInfo || {};
    const comparisonValue = display.comparisonValue || '';
    return `decision-${data.actionType}-${comparisonValue.length}`;
  }
  
  if (nodeType === 'processNode') {
    const display = data.displayInfo || {};
    const contentLength = (display.displayMessage || display.displayPrompt || display.displaySSML || '').length;
    const attributeCount = display.attributeCount || 0;
    const messageCount = display.messageCount || 0;
    
    // UpdateContactAttributes width is driven by the longest "key: value" line and the
    // number of attributes rendered. Key on those so identical attribute sets share a
    // measurement, while genuinely different content still gets a distinct measurement
    // (the previous Date.now()+random key disabled caching entirely and bloated the cache).
    if (data.actionType === 'UpdateContactAttributes') {
      const entries = Object.entries(display.attributes || {});
      const maxAttrLen = entries.reduce(
        (max, [key, value]) => Math.max(max, (key + ': ' + String(value)).length), 0
      );
      return `process-${data.actionType}-${attributeCount || entries.length}-${maxAttrLen}`;
    }
    
    // For MessageParticipantIteratively, include message content length for accurate caching
    if (data.actionType === 'MessageParticipantIteratively' && messageCount > 0) {
      const maxMessageLength = Math.max(
        ...display.messages.map(msg => msg.content.length)
      );
      return `process-${data.actionType}-${contentLength}-${attributeCount}-${messageCount}-${maxMessageLength}`;
    }
    
    return `process-${data.actionType}-${contentLength}-${attributeCount}-${messageCount}`;
  }
  
  return `${nodeType}-default`;
};

/**
 * Creates a measurement container in the DOM
 */
const createMeasurementContainer = () => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.visibility = 'hidden';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '-1';
  document.body.appendChild(container);
  return container;
};

/**
 * Process Node Measurement Component
 */
const ProcessNodeMeasurer = ({ data }) => {
  const colors = categoryColors[data.category] || categoryColors.general;
  const display = data.displayInfo || {};
  
  // Calculate dynamic width based on content - matching ProcessNode logic
  let minWidth = 180;
  if (data.actionType === 'UpdateContactAttributes' && display.attributeCount > 0) {
    const maxAttributeLength = Math.max(
      ...Object.entries(display.attributes || {}).map(([key, value]) => 
        (key + ': ' + String(value)).length
      )
    );
    minWidth = Math.max(200, Math.min(320, maxAttributeLength * 5 + 60));
  } else if (data.actionType === 'MessageParticipantIteratively') {
    if (display.messageCount > 0) {
      const maxMessageLength = Math.max(
        ...display.messages.map(msg => msg.content.length)
      );
      minWidth = Math.max(220, Math.min(400, maxMessageLength * 4 + 120));
    }
  } else if (data.actionType === 'Loop') {
    minWidth = 180;
  } else if (display.displayMessage || display.displayPrompt || display.displaySSML) {
    const textLength = (display.displayMessage || display.displayPrompt || display.displaySSML || '').length;
    minWidth = Math.max(180, Math.min(450, textLength * 4 + 120));
  } else if (display.promptName) {
    const textLength = display.promptName.length;
    minWidth = Math.max(180, Math.min(350, textLength * 8 + 120));
  } else if (display.mediaUri) {
    const textLength = display.mediaUri.length;
    minWidth = Math.max(180, Math.min(400, textLength * 6 + 120));
  }
  
  // Render content exactly like the actual ProcessNode component
  const renderContent = () => {
    switch (data.actionType) {
      case 'UpdateContactAttributes':
        if (display.attributeCount > 0) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {Object.keys(display.attributes || {}).slice(0, 3).map(key => (
                <div key={key} style={{ fontSize: '9px', marginBottom: '1px', wordBreak: 'break-word' }}>
                  <strong>{key}:</strong> {String(display.attributes[key])}
                </div>
              ))}
              {display.attributeCount > 3 && (
                <div style={{ fontSize: '9px', color: '#666' }}>
                  +{display.attributeCount - 3} more...
                </div>
              )}
            </div>
          );
        }
        return null;
        
      case 'InvokeFlowModule':
        if (display.flowModuleName) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {display.flowModuleName}
            </div>
          );
        }
        return null;
        
      case 'CompleteOutboundCall':
        if (display.phoneNumber) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              Number: {display.phoneNumber}
            </div>
          );
        }
        return null;
        
      default:
        // Generic content rendering for other types
        if (display.displayMessage) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              "{display.displayMessage}"
            </div>
          );
        }
        if (display.displayPrompt) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              "{display.displayPrompt}"
            </div>
          );
        }
        // Generic attribute handling for other node types that might have attributes
        if (display.attributeCount > 0) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {Object.keys(display.attributes || {}).slice(0, 3).map(key => (
                <div key={key} style={{ fontSize: '9px', marginBottom: '1px', wordBreak: 'break-word' }}>
                  <strong>{key}:</strong> {String(display.attributes[key])}
                </div>
              ))}
              {display.attributeCount > 3 && (
                <div style={{ fontSize: '9px', color: '#666' }}>
                  +{display.attributeCount - 3} more...
                </div>
              )}
            </div>
          );
        }
        return null;
    }
  };
  
  return (
    <div style={{
      ...baseNodeStyle,
      borderColor: colors.border,
      background: colors.bg,
      minHeight: data.actionType === 'GetParticipantInput' ? '100px' : '80px',
      minWidth: `${minWidth}px`,
      maxWidth: '450px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px', marginRight: '8px' }}>{data.icon}</span>
        <div>
          <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '11px' }}>
            {data.label}
          </div>
          <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
            {data.actionType.replace(/([A-Z])/g, ' $1').trim()}
          </div>
        </div>
      </div>
      
      {/* Render content exactly like the actual ProcessNode */}
      {renderContent()}
    </div>
  );
};

/**
 * Decision Node Measurement Component
 */
const DecisionNodeMeasurer = ({ data }) => {
  const colors = categoryColors[data.category] || categoryColors.validation;
  const display = data.displayInfo || {};
  
  // Adjust size based on content for Compare nodes
  let size = 120;
  if (data.actionType === 'Compare' && display.comparisonValue) {
    const valueLength = display.comparisonValue.length;
    size = Math.max(120, Math.min(180, valueLength * 6 + 120));
  }
  
  return (
    <div style={{
      width: `${size}px`,
      height: `${size}px`,
      background: colors.bg,
      border: `2px solid ${colors.border}`,
      transform: 'rotate(45deg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      visibility: 'hidden'
    }}>
      <div style={{
        transform: 'rotate(-45deg)',
        textAlign: 'center',
        padding: '8px',
        fontSize: '10px',
        lineHeight: '1.2',
        maxWidth: `${size - 40}px`
      }}>
        <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '11px', marginBottom: '4px' }}>
          {data.label}
        </div>
        {display.comparisonValue && (
          <div style={{ fontSize: '9px', color: '#333', wordBreak: 'break-word' }}>
            {display.comparisonValue}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Entry Node Measurement Component
 */
const EntryNodeMeasurer = ({ data }) => (
  <div style={{
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#f5f5f5',
    border: '3px solid #9e9e9e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    visibility: 'hidden'
  }}>
    🏁
  </div>
);

/**
 * Start/End Node Measurement Component
 */
const StartEndNodeMeasurer = ({ data, nodeType }) => {
  const colors = categoryColors[data.category] || categoryColors.general;
  
  return (
    <div style={{
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      background: colors.bg,
      border: `2px solid ${colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '11px',
      visibility: 'hidden'
    }}>
      <span style={{ fontSize: '20px', marginBottom: '4px' }}>{data.icon}</span>
      <div style={{ fontWeight: 'bold', color: colors.icon, textAlign: 'center' }}>
        {data.label}
      </div>
    </div>
  );
};

/**
 * Picks the right measurer component for a node type.
 */
const NodeMeasurer = ({ node }) => {
  switch (node.type) {
    case 'entryNode':
      return <EntryNodeMeasurer data={node.data} />;
    case 'startNode':
    case 'endNode':
      return <StartEndNodeMeasurer data={node.data} nodeType={node.type} />;
    case 'decisionNode':
      return <DecisionNodeMeasurer data={node.data} />;
    case 'processNode':
    default:
      return <ProcessNodeMeasurer data={node.data} />;
  }
};

/**
 * Renders every distinct (uncached) node into ONE hidden container under ONE React
 * root, then measures them all in a single pass. This replaces the previous
 * one-React-root-per-node approach, which was the dominant cost during batch export.
 *
 * `entries` is an array of [cacheKey, representativeNode]. Resolves to a
 * Map<cacheKey, {width, height}>.
 */
const measureBatch = (entries) => new Promise((resolve) => {
  const dims = new Map();
  if (entries.length === 0) {
    resolve(dims);
    return;
  }

  const container = createMeasurementContainer();
  let root;

  const finish = (resultMap) => {
    try { if (root) root.unmount(); } catch (e) { /* ignore */ }
    try { document.body.removeChild(container); } catch (e) { /* container already gone */ }
    resolve(resultMap);
  };

  try {
    root = createRoot(container);
    root.render(
      <>
        {entries.map(([key, node], i) => (
          // inline-block wrapper shrinks to the node so the inner element's rect is exact
          <div key={key} data-measure-idx={i} style={{ position: 'absolute', visibility: 'hidden' }}>
            <NodeMeasurer node={node} />
          </div>
        ))}
      </>
    );

    // React 18 createRoot().render() commits asynchronously, so a single rAF can fire
    // before layout is applied (yielding 0x0 rects). A double rAF guarantees we read
    // after the commit + layout pass.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      entries.forEach(([key, node], i) => {
        const wrapper = container.querySelector(`[data-measure-idx="${i}"]`);
        const element = wrapper && wrapper.firstElementChild;
        let dimensions;

        if (element) {
          const rect = element.getBoundingClientRect();
          dimensions = { width: Math.ceil(rect.width), height: Math.ceil(rect.height) };
          // Guard against a still-uncommitted/empty measurement
          if (!dimensions.width || !dimensions.height) {
            dimensions = getEstimatedDimensions(node);
          }
        } else {
          dimensions = getEstimatedDimensions(node);
        }

        dbg(`📏 ${node.type}:${node.data.actionType || 'default'}: ${dimensions.width}x${dimensions.height}px`);
        dims.set(key, dimensions);
      });

      finish(dims);
    }));
  } catch (error) {
    console.warn('Error measuring nodes, using fallback dimensions:', error);
    entries.forEach(([key, node]) => dims.set(key, getEstimatedDimensions(node)));
    finish(dims);
  }
});

/**
 * Measures multiple nodes. De-duplicates by measurement cache key so identical nodes
 * are only measured once, measures all uncached representatives in a single batch,
 * then maps the results back onto every node id.
 */
export const measureNodes = async (nodes) => {
  const results = new Map();
  const keyByNodeId = new Map();
  const repsByKey = new Map(); // cacheKey -> representative node (uncached only)

  nodes.forEach(node => {
    const cacheKey = getCacheKey(node.type, node.data);
    keyByNodeId.set(node.id, cacheKey);
    if (measurementCache.has(cacheKey)) {
      results.set(node.id, measurementCache.get(cacheKey));
    } else if (!repsByKey.has(cacheKey)) {
      repsByKey.set(cacheKey, node);
    }
  });

  if (repsByKey.size > 0) {
    dbg(`Measuring ${repsByKey.size} distinct node(s) for ${nodes.length} total`);
    const measured = await measureBatch(Array.from(repsByKey.entries()));
    measured.forEach((dimensions, cacheKey) => measurementCache.set(cacheKey, dimensions));
  }

  // Assign a measurement to every node (cache is now populated for all keys).
  nodes.forEach(node => {
    if (results.has(node.id)) return;
    const cacheKey = keyByNodeId.get(node.id);
    results.set(node.id, measurementCache.get(cacheKey) || getEstimatedDimensions(node));
  });

  return results;
};

/**
 * Measures a single node's dimensions (thin wrapper over the batch path).
 */
export const measureNode = async (node) => {
  const cacheKey = getCacheKey(node.type, node.data);
  if (measurementCache.has(cacheKey)) {
    return measurementCache.get(cacheKey);
  }
  const results = await measureNodes([node]);
  return results.get(node.id) || getEstimatedDimensions(node);
};

/**
 * Fallback estimation function (matches existing logic). Exported so the diagram
 * component can share one source of truth for node sizing (see ContactFlowDiagram).
 */
export const getEstimatedDimensions = (node) => {
  let width = 200;
  let height = 100;
  
  if (node.type === 'entryNode') {
    width = height = 80;
  } else if (node.type === 'startNode' || node.type === 'endNode') {
    width = height = 100;
  } else if (node.type === 'decisionNode') {
    const display = node.data.displayInfo || {};
    if (node.data.actionType === 'Compare' && display.comparisonValue) {
      const valueLength = display.comparisonValue.length;
      width = height = Math.max(120, Math.min(180, valueLength * 6 + 120));
    } else {
      width = height = 120;
    }
  } else if (node.type === 'processNode') {
    height = node.data.actionType === 'GetParticipantInput' ? 100 : 80;
    const display = node.data.displayInfo || {};
    if (display.displayMessage || display.displayPrompt) {
      const textLength = (display.displayMessage || display.displayPrompt || '').length;
      width = Math.max(180, Math.min(400, textLength * 4 + 120));
    } else if (node.data.label && node.data.label.length > 20) {
      width = Math.min(300, Math.max(172, node.data.label.length * 8));
    }
  }
  
  return { width, height };
};

/**
 * Clears the measurement cache
 */
export const clearMeasurementCache = () => {
  measurementCache.clear();
};

/**
 * Gets cache statistics
 */
export const getCacheStats = () => ({
  size: measurementCache.size,
  keys: Array.from(measurementCache.keys())
});

/**
 * Pre-warms the cache with common node types for batch exports
 */
export const preWarmCache = async (nodeTypes = []) => {
  const commonNodeTypes = [
    { type: 'entryNode', data: {} },
    { type: 'startNode', data: { actionType: 'SetContactAttributes' } },
    { type: 'endNode', data: { actionType: 'DisconnectParticipant' } },
    { type: 'processNode', data: { actionType: 'MessageParticipant', displayInfo: { displayMessage: 'Hello' } } },
    { type: 'processNode', data: { actionType: 'GetParticipantInput', displayInfo: { displayPrompt: 'Enter option' } } },
    { type: 'decisionNode', data: { actionType: 'Compare', displayInfo: { comparisonValue: 'test' } } },
    ...nodeTypes
  ];
  
  dbg(`Pre-warming cache with ${commonNodeTypes.length} common node types...`);

  const measurements = await Promise.all(
    commonNodeTypes.map(node => measureNode({ id: 'cache-warm', ...node }))
  );

  dbg(`Cache pre-warming completed, ${measurements.length} node types cached`);
  return measurements;
};

/**
 * Optimized cache for batch exports - limits memory usage
 */
export const optimizeForBatchExport = (maxCacheSize = 100) => {
  if (measurementCache.size > maxCacheSize) {
    // Keep most recently used items
    const entries = Array.from(measurementCache.entries());
    measurementCache.clear();
    
    // Keep the last N entries (most recently used)
    entries.slice(-maxCacheSize).forEach(([key, value]) => {
      measurementCache.set(key, value);
    });
    
    dbg(`Cache optimized for batch export, reduced to ${measurementCache.size} entries`);
  }
};

/**
 * Fast batch measurement for identical nodes (common in large flows).
 * `measureNodes` already de-duplicates by measurement key and measures all distinct
 * nodes in a single pass, so this is now a thin alias kept for call-site compatibility.
 */
export const measureSimilarNodes = async (nodes) => measureNodes(nodes);

// Named export for better tree-shaking
export const NodeMeasurement = {
  measureNode,
  measureNodes,
  measureSimilarNodes,
  preWarmCache,
  optimizeForBatchExport,
  clearMeasurementCache,
  getCacheStats
};